import { useMemo, useState } from "react";
import { Calendar, dayjsLocalizer, Views } from "react-big-calendar";
import dayjs from "dayjs";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useSpatial } from "./SpatialContext";
import BulletinNotes from "./BulletinNotes";
import { eventsForDate, toDateKey, isDateKey, timeToMinutes, formatWhen } from "../lib/plannerItems";
import { minutesToTime } from "../lib/dayPlan";
import { isGhostEvent } from "../lib/lumenCalendar";

const localizer = dayjsLocalizer(dayjs);

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const toDate = (dateKey, time) => {
  const [y, m, d] = dateKey.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  return new Date(y, m - 1, d, hh, mm);
};

const oneHourAfter = (time) => minutesToTime(timeToMinutes(time) + 60);

const EMPTY_FORM = { date: "", start_time: "09:00", end_time: "10:00", title: "", description: "" };

const shortTime = (time) => {
  const [h, m] = time.split(":").map(Number);
  const suffix = h < 12 ? "am" : "pm";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hour}${suffix}` : `${hour}:${String(m).padStart(2, "0")}${suffix}`;
};

function EventChip({ event }) {
  return (
    <span>
      <span style={{ opacity: 0.8, marginRight: 4 }}>{shortTime(event.resource.start_time)}</span>
      {event.title}
    </span>
  );
}

const GRID_STYLES = `
  .nq-calendar .rbc-event { font-size: 10px; padding: 1px 4px; }
  .nq-calendar .rbc-event-content { line-height: 1.3; }
  .nq-calendar .rbc-show-more { font-size: 10px; }
  .nq-calendar .rbc-date-cell { font-size: 11px; padding-right: 4px; }
  .nq-calendar .rbc-header { font-size: 10px; padding: 3px 0; }
`;

const GHOST_CHIP_STYLE = {
  backgroundColor: "#E2E8F0", color: "#64748B", border: "1px dashed #94A3B8",
};

const eventStyle = (event) => (isGhostEvent(event.resource) ? { style: GHOST_CHIP_STYLE } : {});

const listRowClass = (event) => (isGhostEvent(event)
  ? "mb-1.5 px-2 py-1.5 rounded-[7px] cursor-pointer bg-slate-50 border border-dashed border-slate-300 border-l-[3px] border-l-slate-300 opacity-80"
  : "mb-1.5 px-2 py-1.5 rounded-[7px] cursor-pointer bg-slate-50 border-l-[3px] border-[#4DB8B5]");

const formFromEvent = (event) => ({
  date: event.date,
  start_time: event.start_time,
  end_time: event.end_time,
  title: event.title,
  description: event.content || "",
});

const fieldStyle = {
  width: "100%",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  padding: "5px 7px",
  fontSize: 11,
  color: "#1E293B",
  fontFamily: "'DM Sans', sans-serif",
  outline: "none",
};

const labelClass = "text-[9px] font-bold text-slate-400 uppercase tracking-[0.06em] mb-1";

export default function CalendarPanel({ onClose }) {
  const { surfaceState, addCalendarEvent, updateCalendarEvent, deleteCalendarEvent } = useSpatial();
  const events = useMemo(() => surfaceState?.calendarEvents || [], [surfaceState]);
  const bulletinItems = surfaceState?.bulletinItems || [];

  const today = new Date();
  const [viewDate, setViewDate] = useState(today);
  const [editing, setEditing] = useState(null);
  const [listedDay, setListedDay] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState(null);

  const calendarEvents = useMemo(
    () => events.map((e) => ({
      id: e.id,
      title: e.title,
      start: toDate(e.date, e.start_time),
      end: toDate(e.date, e.end_time),
      resource: e,
    })),
    [events]
  );

  const todayKey = toDateKey(today);
  const todaysEvents = useMemo(() => eventsForDate(events, todayKey), [events, todayKey]);

  const openNew = (dateKey) => {
    setForm({ ...EMPTY_FORM, date: dateKey });
    setFormError(null);
    setListedDay(null);
    setEditing({ id: null });
  };

  const openExisting = (event) => {
    setForm(formFromEvent(event));
    setFormError(null);
    setListedDay(null);
    setEditing({ id: event.id, ghost: isGhostEvent(event) });
  };

  const closeForm = () => { setEditing(null); setFormError(null); };

  const setField = (name) => (e) => setForm((prev) => ({ ...prev, [name]: e.target.value }));

  const setStartTime = (e) => {
    const start_time = e.target.value;
    setForm((prev) => ({ ...prev, start_time, end_time: start_time ? oneHourAfter(start_time) : prev.end_time }));
  };

  const save = (confirm = false) => {
    if (!form.title.trim()) { setFormError("Give the event a title."); return; }
    const result = editing.id
      ? updateCalendarEvent(editing.id, confirm ? { ...form, status: "confirmed" } : form)
      : addCalendarEvent(form);
    if (result?.ok === false) {
      setFormError("Check the date — the end time has to be after the start time.");
      return;
    }
    closeForm();
  };

  const remove = () => {
    if (editing?.id) deleteCalendarEvent(editing.id);
    closeForm();
  };

  const prevMonth = () => setViewDate((d) => dayjs(d).subtract(1, "month").toDate());
  const nextMonth = () => setViewDate((d) => dayjs(d).add(1, "month").toDate());

  const listedDayEvents = listedDay ? eventsForDate(events, listedDay) : [];
  const listedDayLabel = listedDay
    ? toDate(listedDay, "12:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })
    : "";

  const upcomingItems = bulletinItems
    .filter((n) => isDateKey(n.date) && !n.done)
    .sort((a, b) => `${a.date}${a.time || ""}`.localeCompare(`${b.date}${b.time || ""}`))
    .slice(0, 3);

  return (
    <div className="flex flex-col h-full font-['DM_Sans'] bg-white">

      {/* Main area: sidebar + calendar grid */}
      <div className="flex flex-1 overflow-hidden">

        {/* Left sidebar */}
        <div className="w-[200px] border-r border-black/[0.06] flex flex-col flex-shrink-0 overflow-y-auto">
          <div className="bg-slate-800 px-4 pt-5 pb-4 text-white">
            <div className="text-[10px] font-bold text-white/50 uppercase tracking-[0.08em] mb-1">Today</div>
            <div className="text-5xl font-extrabold leading-none">{today.getDate()}</div>
            <div className="text-[13px] font-semibold text-white/80 mt-0.5">{today.toLocaleDateString("en-US", { weekday: "long" })}</div>
            <div className="text-[11px] text-white/50 mt-px">{today.toLocaleDateString("en-US", { month: "short", year: "numeric" })}</div>
          </div>
          <div className="px-3.5 py-3">
            <div className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.07em] mb-2">Today's Events</div>
            {todaysEvents.length === 0 ? (
              <div className="text-[11px] text-slate-400 italic leading-[1.6] font-['DM_Sans']">
                Nothing scheduled yet.
              </div>
            ) : todaysEvents.map((e) => (
              <div key={e.id} onClick={() => openExisting(e)}
                className={`${listRowClass(e)} transition-all duration-150`}>
                <div className={`text-[11px] font-semibold ${isGhostEvent(e) ? "text-slate-500" : "text-slate-800"}`}>{e.title}</div>
                <div className="text-[9px] text-slate-400 mt-px">{e.start_time} – {e.end_time}{isGhostEvent(e) ? " · suggested" : ""}</div>
              </div>
            ))}
          </div>
          {upcomingItems.length > 0 && (
            <div className="px-3.5 pb-3">
              <div className="text-[9px] font-bold text-slate-400 uppercase tracking-[0.07em] mb-2">Coming Up</div>
              {upcomingItems.map((item, i) => (
                <div key={item.id || i} className="mb-2">
                  <div className="text-[11px] font-semibold text-slate-700">{item.content || item.title}</div>
                  <div className="text-[9px] text-accent-500 mt-px">{formatWhen(item)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Calendar grid */}
        <div className="flex-1 flex flex-col overflow-hidden relative">
          <div className="flex items-center gap-1 px-5 pt-3.5 pb-2.5 border-b border-black/[0.06] flex-shrink-0">
            <button onClick={prevMonth} title="Previous month" aria-label="Previous month"
              className="flex items-center justify-center w-11 h-11 rounded-full bg-none border-none cursor-pointer text-slate-500 text-[34px] leading-none hover:bg-black/[0.05]">
              ‹
            </button>
            <button onClick={nextMonth} title="Next month" aria-label="Next month"
              className="flex items-center justify-center w-11 h-11 rounded-full bg-none border-none cursor-pointer text-slate-500 text-[34px] leading-none hover:bg-black/[0.05]">
              ›
            </button>
            <div className="ml-1.5">
              <span className="text-xl font-bold text-slate-800">{MONTHS[viewDate.getMonth()]}</span>
              <span className="text-base font-normal text-slate-400 ml-2">{viewDate.getFullYear()}</span>
            </div>
            <div className="flex-1" />
            <button onClick={() => openNew(todayKey)}
              className="ml-2 px-2 py-1 rounded-md border border-[#4DB8B5]/40 bg-[#4DB8B5]/10 text-[#4DB8B5] text-[10px] font-bold cursor-pointer">
              + Event
            </button>
            <button onClick={onClose} className="bg-none border-none cursor-pointer text-slate-300 text-lg ml-2">✕</button>
          </div>

          <div className="flex-1 min-h-0 px-3 pb-3 pt-2 nq-calendar">
            <style>{GRID_STYLES}</style>
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              date={viewDate}
              onNavigate={setViewDate}
              defaultView={Views.MONTH}
              views={[Views.MONTH]}
              toolbar={false}
              formats={{ dateFormat: "D" }}
              components={{ event: EventChip }}
              eventPropGetter={eventStyle}
              selectable
              doShowMoreDrillDown={false}
              onShowMore={(dayEvents, date) => setListedDay(toDateKey(date))}
              onSelectSlot={(slot) => openNew(toDateKey(slot.start))}
              onSelectEvent={(slot) => openExisting(slot.resource)}
              style={{ height: "100%" }}
            />
          </div>

          {listedDay && !editing && (
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center p-4" style={{ zIndex: 30 }}
              onClick={() => setListedDay(null)}>
              <div className="bg-white rounded-xl shadow-xl w-[240px] p-3.5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center mb-2.5">
                  <div className="flex-1 text-[12px] font-bold text-slate-800">{listedDayLabel}</div>
                  <button onClick={() => setListedDay(null)} className="bg-none border-none cursor-pointer text-slate-300 text-base">✕</button>
                </div>
                {listedDayEvents.map((e) => (
                  <div key={e.id} onClick={() => openExisting(e)} className={listRowClass(e)}>
                    <div className={`text-[11px] font-semibold ${isGhostEvent(e) ? "text-slate-500" : "text-slate-800"}`}>{e.title}</div>
                    <div className="text-[9px] text-slate-400 mt-px">{e.start_time} – {e.end_time}{isGhostEvent(e) ? " · suggested" : ""}</div>
                  </div>
                ))}
                <button onClick={() => openNew(listedDay)}
                  className="w-full mt-1 py-1.5 rounded-md border border-[#4DB8B5]/40 bg-[#4DB8B5]/10 text-[#4DB8B5] text-[11px] font-bold cursor-pointer">
                  + Event on this day
                </button>
              </div>
            </div>
          )}

          {editing && (
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center p-4" style={{ zIndex: 30 }}>
              <div className="bg-white rounded-xl shadow-xl w-[260px] p-3.5">
                <div className="text-[12px] font-bold text-slate-800 mb-2.5">
                  {editing.ghost ? "Suggested event" : editing.id ? "Edit event" : "New event"}
                </div>
                {editing.ghost && (
                  <div className="text-[10px] text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-md px-2 py-1.5 mb-2.5">
                    Lumen suggested this. It isn't on your calendar until you confirm it.
                  </div>
                )}

                <div className="mb-2">
                  <div className={labelClass}>Date</div>
                  <input type="date" aria-label="Date" value={form.date} onChange={setField("date")} style={fieldStyle} />
                </div>

                <div className="flex gap-2 mb-2">
                  <div className="flex-1">
                    <div className={labelClass}>From</div>
                    <input type="time" aria-label="From" value={form.start_time} onChange={setStartTime} style={fieldStyle} />
                  </div>
                  <div className="flex-1">
                    <div className={labelClass}>To</div>
                    <input type="time" aria-label="To" value={form.end_time} onChange={setField("end_time")} style={fieldStyle} />
                  </div>
                </div>

                <div className="mb-2">
                  <div className={labelClass}>Title</div>
                  <input type="text" aria-label="Title" value={form.title} onChange={setField("title")} placeholder="What is it?" style={fieldStyle} />
                </div>

                <div className="mb-2.5">
                  <div className={labelClass}>Description</div>
                  <textarea rows={3} aria-label="Description" value={form.description} onChange={setField("description")}
                    placeholder="Anything worth remembering" style={{ ...fieldStyle, resize: "none" }} />
                </div>

                {formError && <div className="text-[10px] text-red-500 mb-2">{formError}</div>}

                <div className="flex items-center gap-2">
                  <button onClick={() => save(!!editing.ghost)}
                    className="flex-1 py-1.5 rounded-md bg-[#4DB8B5] text-white text-[11px] font-bold cursor-pointer border-none">
                    {editing.ghost ? "Confirm" : "Save"}
                  </button>
                  <button onClick={closeForm}
                    className="px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-slate-500 text-[11px] font-semibold cursor-pointer">
                    Cancel
                  </button>
                  {editing.id && (
                    <button onClick={remove}
                      className="px-2.5 py-1.5 rounded-md border border-red-100 bg-white text-red-500 text-[11px] font-semibold cursor-pointer">
                      {editing.ghost ? "Dismiss" : "Delete"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Corkboard / Bulletin Board */}
      <div className="flex-shrink-0 border-t-[3px] border-[#8B6340] px-5 pt-3.5 pb-4 relative overflow-hidden min-h-[140px]"
        style={{ background: "linear-gradient(135deg, #C4956A 0%, #A67C52 40%, #B8865E 70%, #C4956A 100%)" }}>
        <div className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: `radial-gradient(ellipse at 20% 30%, rgba(255,255,255,0.06) 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(0,0,0,0.08) 0%, transparent 50%), repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px)` }} />
        <div className="text-[13px] font-bold text-white/80 uppercase tracking-[0.08em] mb-2.5 relative">📌 Bulletin Board</div>
        <BulletinNotes compact />
      </div>
    </div>
  );
}
